import React, { useCallback, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  LopHistoryDateFilter,
  isoToday,
  isoTwoWeeksAgo,
} from '../../components/LopHistoryDateFilter';
import { getAuditLog } from '../../api/auditLog';
import type { AuditLogRow } from '../../types/Monitoring';
import { t } from '../../i18n/t';
import { formatDateTimeIL } from '../../utils/dateTime';

// "יומן פעולות" — read-only viewer of dbo.LopLog, SysAdmin-only. Same
// pattern as AssistantLogPage: LopCrudPage shell + history date filter
// + no-op saveBatch (no Edit permission → toolbar Add/Delete are hidden).

function fmtDateTime(value: unknown): string {
  if (!value) return '';
  return formatDateTimeIL(value, { seconds: true });
}

// Server returns 'ui' | 'assistant' | 'system' — render the Hebrew label.
// Unknown values (future codes not in the map) fall back to the raw string.
const viaLabels: Record<string, string> = {
  ui:        t('auditLog.via.ui'),
  assistant: t('auditLog.via.assistant'),
  system:    t('auditLog.via.system'),
};

const columnDefs: ColDef<AuditLogRow>[] = [
  { field: 'loggedAt', headerName: t('auditLog.columns.loggedAt'), width: 160,
    valueFormatter: (p) => fmtDateTime(p.value) },
  { field: 'userDisplayName', headerName: t('auditLog.columns.user'), width: 140 },
  { field: 'via', headerName: t('auditLog.columns.via'), width: 100,
    valueFormatter: (p) => viaLabels[String(p.value ?? '')] ?? String(p.value ?? '') },
  { field: 'resourceCode', headerName: t('auditLog.columns.resourceCode'), width: 140 },
  { field: 'operation', headerName: t('auditLog.columns.operation'), width: 100 },
  { field: 'entityKey', headerName: t('auditLog.columns.entityKey'), width: 180 },
  { field: 'fundId', headerName: t('auditLog.columns.fundId'), width: 90,
    cellDataType: 'number' },
  { field: 'assetId', headerName: t('auditLog.columns.assetId'), width: 90,
    cellDataType: 'number' },
  { field: 'tenantId', headerName: t('auditLog.columns.tenantId'), width: 90,
    cellDataType: 'number' },
  { field: 'oldValue', headerName: t('auditLog.columns.oldValue'), flex: 1, minWidth: 200,
    tooltipField: 'oldValue' },
  { field: 'newValue', headerName: t('auditLog.columns.newValue'), flex: 1, minWidth: 200,
    tooltipField: 'newValue' },
  { field: 'note', headerName: t('auditLog.columns.note'), flex: 1, minWidth: 200,
    tooltipField: 'note' },
];

const newRowDefaults = (): Partial<AuditLogRow> => ({});

export function AuditLogPage() {
  const [draftFrom, setDraftFrom] = useState<string>(isoTwoWeeksAgo);
  const [draftTo, setDraftTo] = useState<string>(isoToday);
  // No data is loaded until the first click on "Fetch" (see LopCrudPage.awaitingFetch).
  const [fetched, setFetched] = useState<boolean>(false);
  const [activeFrom, setActiveFrom] = useState<string>(draftFrom);
  const [activeTo, setActiveTo] = useState<string>(draftTo);

  const getRows = useCallback(
    () => getAuditLog(activeFrom, activeTo),
    [activeFrom, activeTo],
  );
  const saveBatch = useCallback(async () => [], []);

  return (
    <div className="lop-history-page">
      <LopHistoryDateFilter
        fromDate={draftFrom}
        toDate={draftTo}
        onFromChange={setDraftFrom}
        onToChange={setDraftTo}
        onApply={() => {
          setActiveFrom(draftFrom);
          setActiveTo(draftTo);
          setFetched(true);
        }}
      />
      <LopCrudPage<AuditLogRow>
        awaitingFetch={!fetched}
        resourceCode="auditLog"
        titleKey="auditLog.pageTitle"
        idField="lopLogId"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newRowDefaults}
      />
    </div>
  );
}
